import React from 'react';
import { observer } from '../../shared/observer';
import { isBusy } from '../../shared/busy';
import { router } from '../../shared/hashRouter';
import { curFormat, dateFormat } from '../../shared/format';
import { statusMod, orderAddressLine } from '../../shared/orderStatus';
import { repeatOrder, cancelOrder, changePayType, payOrderOnline } from '../../shared/orderActions';

// Страница заказа — веб-аналог OrderDetailsScreen из клиентского приложения:
// шапка «Заказ № … / дата / Оплачен», строки доставки, чип статуса, трекер из
// трёх шагов, позиции с картинками, итог с бонусами и действия.
//
// Карта курьера из приложения не воспроизводится: Order#driver_coming_info на
// сервере начинается с безусловного `return nil`, поле всегда пустое.
//
// props.id — id заказа из hash-роута #order/<id>.
class OrderDetailsView extends React.Component {
  state = { busy: {}, error: null };

  componentDidMount() {
    // Заказ может быть уже в сторе (пришли со списка) — ensureOrders
    // дедуплицирует и не сходит в сеть второй раз.
    this.props.order.ensureOrders().catch(() => {});
  }

  reload = () => this.props.order.getOrders();

  back = (e) => {
    e.preventDefault();
    router.go('#orders');
  };

  repeat = (id) => () => repeatOrder(this, id);

  // После отмены заказа возвращаемся к списку — как приложение уводит на
  // главный экран.
  cancel = (id) => () => cancelOrder(this, id, () => this.reload().then(() => router.go('#orders')));

  changePayType = (id) => (e) => changePayType(this, id, e.target.value);

  payOnline = (id) => () => payOrderOnline(this, id);

  // Заказ ищем в списке, а если страницу открыли по прямой ссылке и список
  // ещё не приехал — в current_orders из bootstrap токена (тот же payload).
  findOrder() {
    const id = String(this.props.id || '');
    const inList = (this.props.order.orders || []).filter((o) => String(o.id) === id)[0];
    if (inList) return inList;
    const token = this.props.auth.mobileToken || {};
    return (token.current_orders || []).filter((o) => String(o.id) === id)[0] || null;
  }

  render() {
    const store = this.props.order;
    const o = this.findOrder();

    if (!o) {
      if (store.ordersLoading || !store.ordersLoaded) {
        return (
          <div className="ng-order-details">
            <span className="ng-skel ng-skel--block" style={{ height: 120 }} />
          </div>
        );
      }
      return (
        <div className="ng-order-details">
          <p className="ng-checkout__hint">Заказ не найден.</p>
          <a className="btn" href="#orders" onClick={this.back}>К заказам</a>
        </div>
      );
    }

    const cur = (this.props.auth.companyInfo.currency_hash && this.props.auth.companyInfo.currency_hash.symbol_html) || '';
    const mod = statusMod(o);
    const addr = orderAddressLine(o);
    const repeating = isBusy(this, 'repeat:' + o.id);
    const canceling = isBusy(this, 'cancel:' + o.id);
    const payChanging = isBusy(this, 'pay:' + o.id);
    const paying = isBusy(this, 'online:' + o.id);
    const busy = repeating || canceling || payChanging || paying;

    // Кнопка «Оплатить» есть только у заказа с онлайн-способом оплаты —
    // решает сервер (Orders::ClientOrderPayPolicy).
    const canPayOnline = !!(o.online_pay && o.online_pay.allowed);

    const payChange = o.pay_type_change;
    const payTypes = (payChange && payChange.pay_types) || [];
    const canChangePayType = !!(payChange && payChange.allowed && payTypes.length);
    const currentInList = payTypes.some((p) => String(p.id) === String(o.pay_type_id));

    const bonus = Number(o.accure_bonus_sum) || 0;
    const showBonus = bonus > 0 && !!this.props.auth.companyInfo.is_bonus_avaliable;

    return (
      <div className="ng-order-details">
        <header className="ng-order-details__head">
          <div>
            <h1 className="ng-order-details__title">Заказ{o.order_num ? ' № ' + o.order_num : ''}</h1>
            <div className="ng-order-details__date">{dateFormat(o.date)}</div>
          </div>
          <span className={'ng-order-details__paid' + (o.paid ? ' is-paid' : '')}>
            {o.paid ? 'Оплачен' : 'Не оплачен'}
          </span>
        </header>

        <div className="ng-order-details__info">
          {addr ? <div className="ng-order-details__row">{addr}</div> : null}
          {o.time_from && o.time_to ? (
            <div className="ng-order-details__row">{o.time_from} - {o.time_to}</div>
          ) : null}
          {o.pay_type && o.pay_type.title ? (
            <div className="ng-order-details__row">Способ оплаты: {o.pay_type.title}</div>
          ) : null}
        </div>

        <div className="ng-order-details__status">
          <span className={'ng-order__status' + (mod ? ' ng-order__status--' + mod : '')}>{o.status}</span>
        </div>

        <OrderTrack symbol={o.status_symbol} />

        <ul className="ng-order-details__items">
          {(o.items || []).map((it, i) => (
            <li key={i} className="ng-order-details__item">
              <div className="ng-order-details__item-icon ng-img-box ng-img-box--sm">
                {it.icon_url ? <img className="ng-img" src={it.icon_url} alt={it.title} /> : null}
              </div>
              <div className="ng-order-details__item-main">
                <div className="ng-order-details__item-title">{it.title}</div>
                {it.quantity > 0 ? (
                  <span className="ng-order-details__item-qty">{it.quantity} шт</span>
                ) : null}
              </div>
              <div className="ng-order-details__item-price">{curFormat(it.price)} {cur}</div>
            </li>
          ))}
        </ul>

        <div className="ng-order-details__sums">
          <div className="ng-order-details__sum-row">
            <span>Товары</span>
            <strong>{curFormat(o.sum)} {cur}</strong>
          </div>
          {/* Строку «К оплате» показываем, только когда она отличается от суммы
              товаров: списанные бонусы, промокод, залог постамата. */}
          {Number(o.pay_sum) !== Number(o.sum) ? (
            <div className="ng-order-details__sum-row ng-order-details__sum-row--total">
              <span>К оплате</span>
              <strong>{curFormat(o.pay_sum)} {cur}</strong>
            </div>
          ) : null}
          {showBonus ? (
            <div className="ng-order-details__sum-row ng-order-details__sum-row--muted">
              <span>Заработаете бонусов</span>
              <strong>{curFormat(bonus)} {cur}</strong>
            </div>
          ) : null}
        </div>

        {this.state.error ? <div className="order-message">{this.state.error}</div> : null}

        {canChangePayType ? (
          <div className="ng-order-details__paytype">
            Оплата:{' '}
            <select value={o.pay_type_id || ''} disabled={busy} onChange={this.changePayType(o.id)}>
              {!currentInList && (
                <option value={o.pay_type_id || ''} disabled>
                  {(o.pay_type && o.pay_type.title) || 'Способ не выбран'}
                </option>
              )}
              {payTypes.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="ng-order-details__actions">
          {canPayOnline && (
            <button
              type="button"
              className={'btn' + (paying ? ' is-busy' : '')}
              disabled={busy}
              onClick={this.payOnline(o.id)}
            >
              Оплатить
            </button>
          )}
          <button
            type="button"
            className={'btn btn--mini' + (repeating ? ' is-busy' : '')}
            disabled={busy}
            onClick={this.repeat(o.id)}
          >
            Повторить
          </button>
          {o.can_cancel && (
            <button
              type="button"
              className={'btn btn--danger btn--mini' + (canceling ? ' is-busy' : '')}
              disabled={busy}
              onClick={this.cancel(o.id)}
            >
              Отменить заказ
            </button>
          )}
        </div>
      </div>
    );
  }
}

// Трекер из трёх шагов — как в приложении: «принят» → «собран» → «доставка».
// Готовность считается по тем же условиям, что там: waiting_delivery
// закрывает первые два шага, delivered — все три.
function OrderTrack({ symbol }) {
  const delivered = symbol === 'delivered';
  const onTheWay = symbol === 'waiting_delivery';
  const steps = [
    { title: 'Принят', state: delivered || onTheWay ? 'done' : 'current' },
    { title: 'Собран', state: delivered || onTheWay ? 'done' : 'wait' },
    { title: 'Доставка', state: delivered ? 'done' : (onTheWay ? 'current' : 'wait') },
  ];

  return (
    <div className="ng-order-track">
      {steps.map((st, i) => (
        <div key={st.title} className={'ng-order-track__step is-' + st.state}>
          {i > 0 ? <span className="ng-order-track__line" /> : null}
          <span className="ng-order-track__dot">{st.state === 'done' ? '✓' : ''}</span>
          <span className="ng-order-track__title">{st.title}</span>
        </div>
      ))}
    </div>
  );
}

export const OrderDetails = observer(OrderDetailsView, ['orderStore', 'cartStore', 'authStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Profile/OrderDetails.jsx